import { zodResolver } from "@hookform/resolvers/zod";
import {
  createFileRoute,
  useNavigate,
  useRouter,
  useSearch,
} from "@tanstack/react-router";
import { useAtom } from "jotai";
import { useEffect, type JSX } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { AuthPageLayout } from "@/components/auth/auth-page-layout";
import { AuthFlowContinueButton } from "@/components/ui/auth-flow/auth-flow-continue-button";
import { AuthFlowTitle } from "@/components/ui/auth-flow/auth-flow-title";
import { DuplicateCpfErrorDrawer } from "@/components/ui/drawer/duplicate-cpf-error-drawer";
import { UnexpectedErrorDrawer } from "@/components/ui/drawer/unexpected-error-drawer";
import { FlowHeader } from "@/components/ui/flow-header/flow-header";
import {
  FormControl,
  FormField,
  FormHandlerSubmit,
  FormItem,
  FormRoot,
} from "@/components/ui/form/form";
import { InputCpf } from "@/components/ui/input/input-cpf";
import { useSendEmailOtp } from "@/hooks/use-send-email-otp";
import { firstAccessAtom } from "@/lib/atoms/first-access-atom";
import {
  captureAuthError,
  trackFirstAccessDocumentDuplicate,
  trackFirstAccessDocumentNotFound,
  trackFirstAccessOtpSendFailed,
  trackFirstAccessOtpSent,
  trackFirstAccessStarted,
  trackFirstAccessStepViewed,
} from "@/lib/auth/auth-tracking";
import {
  type FirstAccessDocumentFormFields,
  firstAccessDocumentSchema,
} from "@/lib/form-schemas/first-access-document-schema";

const CPF_FORMATTED_LENGTH = 14; // "000.000.000-00"

const FirstAccessDocumentPage = (): JSX.Element => {
  const navigate = useNavigate();
  const router = useRouter();
  const searchParams = useSearch({ from: "/auth/first-access/document" });

  const [firstAccess, setFirstAccess] = useAtom(firstAccessAtom);

  const { sendOtp, isPending } = useSendEmailOtp();

  useEffect(() => {
    trackFirstAccessStarted();
    trackFirstAccessStepViewed("documento");
  }, []);

  const form = useForm<FirstAccessDocumentFormFields>({
    resolver: zodResolver(firstAccessDocumentSchema),
    mode: "onChange",
    defaultValues: {
      document: firstAccess.document,
    },
  });

  const documentValue = form.watch("document");
  const isDocumentValid =
    form.formState.isValid && documentValue.length === CPF_FORMATTED_LENGTH;

  const handleContinue = async (data: FirstAccessDocumentFormFields): Promise<void> => {
    try {
      const { email, isFirstAccess, notFound, methodId, multipleUsersFound } =
        await sendOtp(data.document);

      if (notFound) {
        trackFirstAccessDocumentNotFound();
        void navigate({ to: "/auth/first-access/document-not-found" });
        return;
      }

      if (multipleUsersFound) {
        trackFirstAccessDocumentDuplicate();
        void navigate({
          to: "/auth/first-access/document",
          search: { action: "duplicate-cpf" },
        });
        return;
      }

      if (isFirstAccess) {
        trackFirstAccessOtpSent();

        setFirstAccess((prev) => ({
          ...prev,
          document: data.document,
          email,
          methodId,
        }));

        void navigate({ to: "/auth/first-access/verify-email" });
      } else {
        void navigate({ to: "/auth/signin" });
      }
    } catch (error) {
      trackFirstAccessOtpSendFailed(error);
      captureAuthError(error, { flow: "primeiro-acesso", step: "documento" });
      showUnexpectedErrorDrawer();
    }
  };

  const showDuplicateCpfDrawer = (): void => {
    void navigate({
      to: "/auth/first-access/document",
      search: { action: "duplicate-cpf" },
    });
  };

  const hideDuplicateCpfDrawer = (): void => {
    if (searchParams.action === "duplicate-cpf") {
      router.history.back();
    }
  };

  const showUnexpectedErrorDrawer = (): void => {
    void navigate({
      to: "/auth/first-access/document",
      search: { action: "error" },
    });
  };

  const hideUnexpectedErrorDrawer = (): void => {
    if (searchParams.action === "error") {
      router.history.back();
    }
  };

  return (
    <AuthPageLayout>
      <FlowHeader>
        <FlowHeader.Actions>
          <FlowHeader.BackButton />
          <FlowHeader.HelpButton />
        </FlowHeader.Actions>
        <FlowHeader.ProgressBar progress={0.25} />
      </FlowHeader>

      {/* Page content */}
      <FormRoot {...form}>
        <FormHandlerSubmit
          handleSubmit={handleContinue}
          className="flex flex-col flex-1 p-6 gap-6 w-full"
        >
          <AuthFlowTitle>
            <AuthFlowTitle.Heading>
              Digite seu CPF para iniciar seu acesso com segurança
            </AuthFlowTitle.Heading>
          </AuthFlowTitle>

          <div className="flex-1">
            <FormField
              control={form.control}
              name="document"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <InputCpf
                      title=""
                      placeholder="000.000.000-00"
                      defaultValue={field.value}
                      onChange={(value) => {
                        if (typeof value === "string") {
                          field.onChange(value);
                        }
                      }}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          <AuthFlowContinueButton
            type="submit"
            loading={isPending}
            disabled={!isDocumentValid}
          />
        </FormHandlerSubmit>
      </FormRoot>
      <UnexpectedErrorDrawer
        open={searchParams.action === "error"}
        setOpen={(isOpen) => {
          if (isOpen) showUnexpectedErrorDrawer();
          else hideUnexpectedErrorDrawer();
        }}
      />

      <DuplicateCpfErrorDrawer
        open={searchParams.action === "duplicate-cpf"}
        setOpen={(isOpen) => {
          if (isOpen) showDuplicateCpfDrawer();
          else hideDuplicateCpfDrawer();
        }}
      />
    </AuthPageLayout>
  );
};

const firstAccessDocumentSearchSchema = z.object({
  action: z.string().nullable().optional(),
});

export const Route = createFileRoute("/auth/first-access/document")({
  component: FirstAccessDocumentPage,
  validateSearch: firstAccessDocumentSearchSchema,
});
